import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import { LATEST, TimePoint } from "../../../../../utils/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import {
  baseTimePointOptions,
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";
import ToolsPerformanceChart from "../ToolsPerformanceWidget/ToolsPerformanceChart";

const calculateComplianceTrendData = (currentData, baseData) => {
  if (!currentData || !baseData) return {};
  const trends = {};

  Object.keys(currentData).forEach((toolType) => {
    const current = currentData[toolType];
    const base = baseData[toolType];

    if (current && base && current.all > 0 && base.all > 0) {
      // Calculate compliance percentages
      const currentComplianceRate = (current.resolved / current.all) * 100;
      const baseComplianceRate = (base.resolved / base.all) * 100;
      const complianceRateDiff = currentComplianceRate - baseComplianceRate;
      trends[toolType] = {
        currentComplianceRate: currentComplianceRate.toFixed(1),
        baseComplianceRate: baseComplianceRate.toFixed(1),
        diff: complianceRateDiff.toFixed(1),
        percentChange:
          baseComplianceRate > 0
            ? ((complianceRateDiff / baseComplianceRate) * 100).toFixed(1)
            : 0,
        isPositive: complianceRateDiff >= 0,
        compliantControls: {
          current: current.resolved,
          previous: base.resolved,
        },
        allControls: {
          current: current.all,
          previous: base.all,
        },
      };
    }
  });

  return trends;
};

const ToolsPerformanceTrendWidget = forwardRef(({ mainTimePoint }, ref) => {
  const { getToolsPerformanceTrend, trackEvent } = useAPI();
  const widgetContentRef = useRef(null);
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);

  const {
    data: toolsPerformanceTrend,
    isLoading: isLoadingToolsPerformanceTrend,
  } = useQuery({
    queryKey: dashboardKeys.widgetType("toolsPerformanceTrend"),
    queryFn: getToolsPerformanceTrend,
    select: (data) => data.data,
  });

  useEffect(() => {
    if (toolsPerformanceTrend && toolsPerformanceTrend[mainTimePoint]) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint, toolsPerformanceTrend]);
  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    toolsPerformanceTrend,
    icons,
    trackEvent,
    "tools-performance"
  );

  const trendData = useMemo(() => {
    if (baseTimePoint === LATEST) return null;
    return calculateComplianceTrendData(
      toolsPerformanceTrend[LATEST],
      toolsPerformanceTrend[baseTimePoint]
    );
  }, [toolsPerformanceTrend, baseTimePoint]);

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || isLoadingToolsPerformanceTrend) {
        return null;
      }

      if (!isCombinedExport) {
        trackEvent(
          "User clicked on Export to PDF button in Security Controls Efficiency"
        );
      }

      try {
        // Common logic: create subtitle and prepare widget content
        const subtitle =
          baseTimePoint !== LATEST
            ? `Current count vs. ${baseTimePointOptions[baseTimePoint]}`
            : "";

        // Clone and style widget content (shared between both exports)
        const widgetClone = widgetContentRef.current.cloneNode(true);
        widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
        `;

        // Expand the scroll container to show all tools (shared logic)
        const scrollContainer = widgetClone.querySelector(".scroll-container");
        if (scrollContainer instanceof HTMLElement) {
          // Convert scroll container to a grid layout showing all items
          scrollContainer.style.cssText = `
            display: grid !important;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
            gap: 16px !important;
            width: 100% !important;
            overflow: visible !important;
            padding: 16px !important;
            max-width: none !important;
            align-items: start !important;
            justify-content: center !important;
            grid-auto-rows: auto !important;
            height: auto !important;
            max-height: none !important;
          `;

          // Ensure all tool items are visible and properly sized
          const toolItems = scrollContainer.children;
          Array.from(toolItems).forEach((item) => {
            if (item instanceof HTMLElement) {
              item.style.cssText += `
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: center !important;
                width: 200px !important;
                height: auto !important;
                min-height: 200px !important;
                max-width: 200px !important;
                overflow: visible !important;
                margin: 0 !important;
                padding: 8px !important;
                transform: none !important;
                opacity: 1 !important;
                visibility: visible !important;
                flex-shrink: 0 !important;
              `;
            }
          });
        }

        // Hide arrow buttons (shared logic)
        const arrows = widgetClone.querySelectorAll(
          "button, .MuiIconButton-root"
        );
        arrows.forEach((arrow) => {
          if (arrow instanceof HTMLElement) {
            arrow.style.display = "none";
          }
        });

        if (isCombinedExport) {
          // For combined export, return just the widget content with header
          const widgetContainer = document.createElement("div");

          const widgetHeader = createPdfHeader(
            "Security Controls Efficiency Report",
            subtitle
          );
          widgetContainer.appendChild(widgetHeader);
          widgetContainer.appendChild(widgetClone);
          return widgetContainer;
        } else {
          // Individual export using html2pdf approach
          const entirePageElement = await createPdfContainer();

          const header = createPdfHeader(
            "Security Controls Efficiency Report",
            subtitle
          );
          entirePageElement.appendChild(header);
          entirePageElement.appendChild(widgetClone);

          // Measure content dimensions
          const { contentWidth, contentHeight } = await measureContentForPdf(
            entirePageElement
          );

          // Generate PDF using html2pdf (no compression, high quality)
          const pdfConfig = createPdfConfig(
            generateFileName(
              "Breeze_Security_Controls_Efficiency",
              null,
              FILE_EXTENSIONS.PDF
            ),
            contentWidth,
            contentHeight
          );

          await html2pdf().set(pdfConfig).from(entirePageElement).save();
        }
      } catch (error) {
        Toast.error(
          "Failed to export Security Controls Efficiency, Please try again later"
        );
        return null;
      }
    },
    [baseTimePoint, isLoadingToolsPerformanceTrend, trackEvent]
  );

  // Expose the export function to parent components
  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "toolsPerformanceTrend",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );

  return (
    <WidgetWrapper
      title={"Security Controls Efficiency"}
      sortMenuItems={{
        data: [...sortMenuItems],
        tooltip: "View changes since selected time",
      }}
      info="How many controls are compliant"
      handleExportToPdf={() => handleExportToPdf(false)}
      viewAll={{
        handleViewAll: () => {
          window.open("/posture/controls", "_blank");
        },
        tooltip: "View all tools performance data",
        newTab: true,
      }}
    >
      <Box ref={widgetContentRef}>
        {isLoadingToolsPerformanceTrend ? (
          <LoadingIcon />
        ) : Object.keys(toolsPerformanceTrend).length === 0 ? (
          <Box>
            <Typography variant="caption" marginLeft={2.5}>
              No data available
            </Typography>
          </Box>
        ) : (
          <Box>
            {" "}
            <Typography variant="caption" marginLeft={2.5}>
              Current count{" "}
              {baseTimePoint !== LATEST
                ? `vs. ${baseTimePointOptions[baseTimePoint]}`
                : ""}
            </Typography>
            <ToolsPerformanceChart
              toolsPerformance={toolsPerformanceTrend[LATEST]}
              height={300}
              showTitles={false}
              shouldBeLoaded={!isLoadingToolsPerformanceTrend}
              sx={{
                boxShadow: "none",
              }}
              trendData={trendData}
              isPdfExport={false}
            />
          </Box>
        )}
      </Box>
    </WidgetWrapper>
  );
});

ToolsPerformanceTrendWidget.displayName = "ToolsPerformanceTrendWidget";

export default ToolsPerformanceTrendWidget;
